import React, { useState, useEffect, useCallback } from 'react';
import {
  BuildingOfficeIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  PlusCircleIcon,
  GlobeAmericasIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ChartBarIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  KeyIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import axiosInstance from '../utils/axios';
import toast from 'react-hot-toast';
import { PageHeader, StatCard, ConfirmDialog } from '../components/common';
import DeviceIcon from '../components/DeviceIcon';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../config/permissions';
import VendorUsersModal from '../components/sellers/VendorUsersModal';

// Inventory and Direct are our own records, seeded when the system is set up.
// Neither is a real outside company, so both stay read-only on this page.
const isHouseRecord = (role) => role === 'inventory' || role === 'direct';

const Sellers = () => {
  const { user } = useAuth();
  const isAdmin = hasRole(user?.role, 'admin');
  const [sellers, setSellers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create, edit
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [vendorUsersSeller, setVendorUsersSeller] = useState(null); // seller whose logins are open
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    role: 'dealer',
    address: '',
    city: '',
    state: '',
    country: '',
    countryCode: '',
    phone: '',
    email: '',
    website: '',
  });

  // Fetch all sellers and devices
  const fetchSellersAndDevices = useCallback(async () => {
    try {
      setLoading(true);
      const [sellersResponse, devicesResponse] = await Promise.all([
        axiosInstance.get('/sellers'),
        axiosInstance.get('/device-details/complete')
      ]);
      setSellers(sellersResponse.data.data || []);
      setDevices(devicesResponse.data.devices || []);
    } catch (error) {
      toast.error('Failed to fetch data');
      setSellers([]);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSellersAndDevices();
  }, [fetchSellersAndDevices]);

  // Get device count for a seller
  const getDeviceCount = (sellerId) => {
    return devices.filter(d => d.soldBy?._id === sellerId || d.soldBy === sellerId).length;
  };

  // Devices tied to a vendor either way round: ones it sold, and ones built on
  // a dryer it makes. Both are links on the device record, so a vendor with
  // any of them can only be deactivated - deleting it would leave those
  // devices pointing at a company that no longer exists.
  const getLinkedDeviceCount = (sellerId) =>
    devices.filter(
      (d) =>
        d.soldBy?._id === sellerId ||
        d.soldBy === sellerId ||
        d.dryerManufacturer?._id === sellerId ||
        d.dryerManufacturer === sellerId
    ).length;

  // Calculate statistics
  const getStatistics = () => {
    const stats = {
      total: sellers.length,
      active: sellers.filter(s => s.isActive !== false).length,
      inactive: sellers.filter(s => s.isActive === false).length,
      totalDevicesSold: 0,
      byRole: {
        dealer: sellers.filter(s => s.role === 'dealer').length,
        oem: sellers.filter(s => s.role === 'oem').length,
        integrator: sellers.filter(s => s.role === 'integrator').length,
        direct: sellers.filter(s => s.role === 'direct').length,
      },
      byCountry: {}
    };

    // Count total devices sold (excluding inventory)
    stats.totalDevicesSold = devices.filter(d =>
      d.soldBy &&
      d.soldBy !== 'inventory' &&
      sellers.some(s => s._id === (d.soldBy._id || d.soldBy) && s.role !== 'inventory')
    ).length;

    // Count by country
    sellers.forEach(seller => {
      const country = seller.country || 'Unknown';
      stats.byCountry[country] = (stats.byCountry[country] || 0) + 1;
    });

    return stats;
  };

  const stats = getStatistics();

  // Get unique countries for filter
  const uniqueCountries = [...new Set(sellers.map(s => s.country || 'Unknown'))].sort();

  // Filter and sort sellers (inventory and direct first)
  const filteredSellers = sellers
    .filter(seller => {
      const matchesSearch =
        seller.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        seller.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        seller.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        seller.phone?.includes(searchTerm);

      const matchesRole = roleFilter === 'all' || seller.role === roleFilter;
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && seller.isActive !== false) ||
        (statusFilter === 'inactive' && seller.isActive === false);
      const matchesCountry =
        countryFilter === 'all' ||
        (seller.country || 'Unknown') === countryFilter;

      return matchesSearch && matchesRole && matchesStatus && matchesCountry;
    })
    .sort((a, b) => {
      // Priority order: inventory first, then direct, then others
      const roleOrder = { 'inventory': 0, 'direct': 1, 'oem': 2, 'integrator': 3, 'dealer': 4 };
      const orderA = roleOrder[a.role] ?? 5;
      const orderB = roleOrder[b.role] ?? 5;

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // If same role, sort alphabetically by name
      return (a.name || '').localeCompare(b.name || '');
    });

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (modalMode === 'create') {
        await axiosInstance.post('/sellers', formData);
        toast.success('Vendor created successfully');
      } else if (modalMode === 'edit') {
        await axiosInstance.put(`/sellers/${selectedSeller._id}`, formData);
        toast.success('Vendor updated successfully');
      }

      fetchSellersAndDevices();
      closeModal();
    } catch (error) {
      if (error.response?.data?.message?.includes('duplicate')) {
        toast.error('A vendor with this name already exists');
      } else {
        toast.error(error.response?.data?.message || 'Operation failed');
      }
    }
  };

  // Open modal
  const openModal = (mode, seller = null) => {
    setModalMode(mode);
    setSelectedSeller(seller);

    if (mode === 'edit' && seller) {
      setFormData({
        name: seller.name || '',
        role: seller.role || 'dealer',
        address: seller.address || '',
        city: seller.city || '',
        state: seller.state || '',
        country: seller.country || '',
        countryCode: seller.countryCode || '',
        phone: seller.phone || '',
        email: seller.email || '',
        website: seller.website || '',
      });
    } else {
      setFormData({
        name: '',
        role: 'dealer',
        address: '',
        city: '',
        state: '',
        country: '',
        countryCode: '',
        phone: '',
        email: '',
        website: '',
      });
    }
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedSeller(null);
    setFormData({
      name: '',
      role: 'dealer',
      address: '',
      city: '',
      state: '',
      country: '',
      countryCode: '',
      phone: '',
      email: '',
      website: '',
    });
  };

  // Toggle active status
  const toggleActive = async (id) => {
    try {
      const response = await axiosInstance.put(`/sellers/${id}/toggle-active`);
      toast.success(response.data.message);
      fetchSellersAndDevices();
    } catch (error) {
      toast.error('Failed to toggle active status');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const response = await axiosInstance.delete(`/sellers/${deleteTarget._id}`);
      toast.success(response.data.message);
      setDeleteTarget(null);
      fetchSellersAndDevices();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete vendor');
    } finally {
      setDeleting(false);
    }
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'oem':
        return 'bg-purple-100 text-purple-800';
      case 'dealer':
        return 'bg-blue-100 text-blue-800';
      case 'integrator':
        return 'bg-green-100 text-green-800';
      case 'direct':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        subtitle="Manage OEMs, dealers and integrators"
        actions={
          <button
            onClick={() => openModal('create')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusCircleIcon className="h-5 w-5" />
            Add Vendor
          </button>
        }
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <StatCard
          label="Total Vendors"
          value={stats.total}
          icon={UserGroupIcon}
          delay={0.1}
        />
        <StatCard
          label="Active"
          value={stats.active}
          valueColor="text-green-600"
          icon={CheckCircleIcon}
          iconColor="text-green-400"
          delay={0.15}
        />
        <StatCard
          label="Devices Sold"
          value={stats.totalDevicesSold}
          valueColor="text-indigo-600"
          icon={DeviceIcon}
          iconColor="text-indigo-400"
          delay={0.2}
        />
        <StatCard
          label="Dealers"
          value={stats.byRole.dealer}
          valueColor="text-blue-600"
          icon={BuildingOffice2Icon}
          iconColor="text-blue-400"
          delay={0.25}
        />
        <StatCard
          label="OEMs"
          value={stats.byRole.oem}
          valueColor="text-purple-600"
          icon={ChartBarIcon}
          iconColor="text-purple-400"
          delay={0.3}
        />
        <StatCard
          label="Integrators"
          value={stats.byRole.integrator}
          valueColor="text-green-600"
          icon={BuildingOfficeIcon}
          iconColor="text-green-400"
          delay={0.35}
        />
        <StatCard
          label="Countries"
          value={Object.keys(stats.byCountry).length}
          valueColor="text-indigo-600"
          icon={GlobeAmericasIcon}
          iconColor="text-indigo-400"
          delay={0.4}
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Role Filter */}
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              <option value="all">All Roles</option>
              <option value="dealer">Dealer</option>
              <option value="oem">OEM</option>
              <option value="integrator">Integrator</option>
              <option value="direct">Direct</option>
              <option value="inventory">Inventory</option>
            </select>
            <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          </div>

          {/* Country Filter */}
          <div className="relative">
            <GlobeAmericasIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              <option value="all">All Countries</option>
              {uniqueCountries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Vendor list */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredSellers.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <BuildingOfficeIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <p className="text-gray-600">No vendors found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Devices
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSellers.map((seller) => (
                  <tr key={seller._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-600 font-medium text-xs">
                            {seller.name?.substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {seller.name}
                          </div>
                          {seller.website && (
                            <div className="text-xs text-blue-600">
                              <a href={`http://${seller.website}`} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                {seller.website}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {seller.city && seller.state ? (
                          <div className="flex items-center gap-1">
                            <MapPinIcon className="h-3 w-3 text-gray-400" />
                            {seller.city}, {seller.state}
                          </div>
                        ) : seller.city ? (
                          <div className="flex items-center gap-1">
                            <MapPinIcon className="h-3 w-3 text-gray-400" />
                            {seller.city}
                          </div>
                        ) : null}
                        {seller.country && (
                          <div className="text-xs text-gray-500">{seller.country}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {seller.phone && (
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <PhoneIcon className="h-3 w-3" />
                            {seller.phone}
                          </div>
                        )}
                        {seller.email && (
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <EnvelopeIcon className="h-3 w-3" />
                            {seller.email}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(seller.role)}`}>
                        {seller.role?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <DeviceIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm font-semibold text-gray-900">{getDeviceCount(seller._id)}</span>
                        <span className="text-xs text-gray-500 ml-1">devices</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isHouseRecord(seller.role) ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <CheckCircleIcon className="h-3 w-3" />
                          Always Active
                        </span>
                      ) : (
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            seller.isActive !== false
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {seller.isActive !== false ? (
                            <>
                              <CheckCircleIcon className="h-3 w-3" />
                              Active
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="h-3 w-3" />
                              Inactive
                            </>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {isHouseRecord(seller.role) ? (
                        <span className="text-gray-400 text-xs">Protected</span>
                      ) : (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => openModal('edit', seller)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setVendorUsersSeller(seller)}
                            className="text-amber-600 hover:text-amber-800"
                            title="Vendor logins"
                          >
                            <KeyIcon className="h-4 w-4" />
                          </button>
                          {isAdmin && (
                            getLinkedDeviceCount(seller._id) > 0 ? (
                              <button
                                onClick={() => toggleActive(seller._id)}
                                className={
                                  seller.isActive !== false
                                    ? 'text-red-600 hover:text-red-800'
                                    : 'text-green-600 hover:text-green-800'
                                }
                                title={
                                  seller.isActive !== false
                                    ? 'Deactivate only - a vendor that has sold a unit, or is the manufacturer of one, cannot be deleted'
                                    : 'Activate vendor'
                                }
                              >
                                {seller.isActive !== false ? (
                                  <XCircleIcon className="h-4 w-4" />
                                ) : (
                                  <CheckCircleIcon className="h-4 w-4" />
                                )}
                              </button>
                            ) : (
                              <button
                                onClick={() => setDeleteTarget(seller)}
                                className="text-red-600 hover:text-red-800"
                                title="Delete"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Vendor logins */}
      {vendorUsersSeller && (
        <VendorUsersModal
          seller={vendorUsersSeller}
          onClose={() => setVendorUsersSeller(null)}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this vendor?"
        message={
          <>
            <p>
              <strong>{deleteTarget?.name}</strong> will be permanently deleted,
              along with every login under it and all of its company data.
            </p>
            <p>
              Anyone signed into the vendor area for this company will be cut
              off straight away. This cannot be undone.
            </p>
          </>
        }
        confirmLabel="Delete Vendor"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {modalMode === 'create' ? 'Add New Vendor' : 'Edit Vendor'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter vendor name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="dealer">Dealer</option>
                    <option value="oem">OEM</option>
                    <option value="integrator">Integrator</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Phone number"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Street address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="City"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State/Province
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="State or Province"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Country"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country Code
                  </label>
                  <input
                    type="text"
                    value={formData.countryCode}
                    onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., US, CA, GB"
                    maxLength="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    type="text"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="www.example.com"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {modalMode === 'create' ? 'Create Vendor' : 'Update Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sellers;
